import React, { useState, useEffect } from 'react';
import nodeLibrary from './nodeLibrary.json';

// Single source of truth: nodeLibrary.json
const { nodes, typeColors, categories, blankNode: blankNodeTemplate } = nodeLibrary;

// localStorage key for favorites
const FAVORITES_KEY = 'bonzai-node-favorites';

function loadFavorites() {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveFavorites(ids) {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids));
}

// Export for use in WorkflowEditor
export { nodes, typeColors };

function NodePanel({ isOpen, onClose, onSelectNode, initialSection = null }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState(initialSection); // null = menu, 'favorites' | 'skills' | category type
  const [favoriteIds, setFavoriteIds] = useState(loadFavorites);
  const [searchFocused, setSearchFocused] = useState(false);

  // Reset to initialSection when panel opens
  useEffect(() => {
    if (isOpen) {
      setActiveSection(initialSection);
      setSearchQuery('');
    }
  }, [isOpen, initialSection]);

  useEffect(() => {
    saveFavorites(favoriteIds);
  }, [favoriteIds]);

  const toggleFavorite = (nodeId, e) => {
    e.stopPropagation();
    setFavoriteIds(prev =>
      prev.includes(nodeId) ? prev.filter(id => id !== nodeId) : [...prev, nodeId]
    );
  };

  const query = searchQuery.trim().toLowerCase();
  const filteredNodes = query
    ? nodes.filter(n =>
        n.name.toLowerCase().includes(query) ||
        n.description.toLowerCase().includes(query)
      )
    : nodes;
  const showCustomInSearch = query && 'custom skill'.includes(query);

  const favoriteNodes = nodes.filter(n => favoriteIds.includes(n.id));

  const makeBlankNode = () => ({
    ...blankNodeTemplate,
    id: `custom-skill-${Date.now()}`
  });

  const handleNodeClick = (node) => {
    if (onSelectNode) {
      onSelectNode({
        ...node,
        color: typeColors[node.type] || '#6b7280'
      });
    }
  };

  if (!isOpen) return null;

  const isSearching = searchQuery.trim().length > 0;

  // Determine title based on active section
  const activeCat = categories.find(c => c.type === activeSection);
  const sectionTitle = activeSection === 'favorites' && !isSearching ? 'Favorites' :
    activeSection === 'skills' && !isSearching ? 'Skills' :
    activeCat && !isSearching ? activeCat.label :
    'Add a node';

  // Whether we're in a category detail view
  const inCategoryView = activeCat != null;
  const canGoBack = (activeSection === 'favorites' || activeSection === 'skills' || inCategoryView) && !isSearching;

  return (
    <div style={{
      width: '340px',
      height: '100%',
      background: '#fff',
      borderLeft: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: '56px',
      flexShrink: 0
    }}>
      {/* Header */}
      <div style={{ padding: '20px 24px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {canGoBack && (
              <button
                onClick={() => setActiveSection(inCategoryView ? 'skills' : null)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#6b7280',
                  borderRadius: '4px',
                  transition: 'color 0.15s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#111827'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '18px', height: '18px' }}>
                  <path fillRule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clipRule="evenodd" />
                </svg>
              </button>
            )}
            <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', margin: 0 }}>
              {sectionTitle}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: '#9ca3af',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#6b7280'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '20px', height: '20px' }}>
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '0 24px 16px', borderBottom: '1px solid #f3f4f6' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 14px',
          background: '#fff',
          border: searchFocused ? '2px solid #8b5cf6' : '1px solid #e5e7eb',
          borderRadius: '8px',
          transition: 'border-color 0.15s ease'
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#9ca3af" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
            <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
          </svg>
          <input
            type="text"
            placeholder="Search nodes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            autoFocus
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: '14px',
              color: '#111827'
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                background: 'none',
                border: 'none',
                padding: '2px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: '#9ca3af'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isSearching ? (
          /* Search results — flat across all categories + custom skill */
          (filteredNodes.length > 0 || showCustomInSearch) ? (
            <>
              {showCustomInSearch && <BlankNodeItem onClick={() => handleNodeClick(makeBlankNode())} />}
              {filteredNodes.map(node => (
                <NodeListItem
                  key={node.id}
                  node={node}
                  onClick={() => handleNodeClick(node)}
                  isFavorited={favoriteIds.includes(node.id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </>
          ) : (
            <div style={{ padding: '32px 24px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
              No nodes found for "{searchQuery}"
            </div>
          )
        ) : activeSection === null ? (
          /* Top-level menu: Favorites + Skills */
          <div style={{ padding: '8px 0' }}>
            <SectionHeader
              title="Favorites"
              description="Your saved skills"
              icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#facc15" style={{ width: '20px', height: '20px' }}><path fillRule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401Z" clipRule="evenodd" /></svg>}
              iconBg="#fefce8"
              count={favoriteNodes.length}
              onClick={() => setActiveSection('favorites')}
            />
            <SectionHeader
              title="Skills"
              description="Use skills to shape how Claude codes"
              icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#8b5cf6" style={{ width: '20px', height: '20px' }}><path d="M12 4.467c0-.405.262-.75.559-1.027.276-.257.441-.584.441-.94 0-.828-.895-1.5-2-1.5s-2 .672-2 1.5c0 .362.171.694.456.953.29.265.544.6.544.994a.968.968 0 0 1-1.024.974 39.655 39.655 0 0 1-3.014-.306.75.75 0 0 0-.848.847c.14.993.242 1.999.306 3.014A.968.968 0 0 1 4.447 10c-.393 0-.729-.253-.994-.544C3.194 9.17 2.862 9 2.5 9 1.672 9 1 9.895 1 11s.672 2 1.5 2c.356 0 .683-.165.953-.441.277-.297.622-.559 1.027-.559a.997.997 0 0 1 1.004 1.03 39.747 39.747 0 0 1-.319 3.02.75.75 0 0 0 .848.847 39.69 39.69 0 0 0 3.014-.306A.968.968 0 0 0 10 15.553c0-.393-.253-.729-.544-.994C9.17 14.306 9 13.862 9 13.5c0-.828.895-1.5 2-1.5s2 .672 2 1.5c0 .362-.171.694-.456.953-.29.265-.544.6-.544.994a.968.968 0 0 0 1.024.974 39.655 39.655 0 0 0 3.014-.306.75.75 0 0 0 .848-.847 39.659 39.659 0 0 0-.306-3.014.968.968 0 0 1 .974-1.024c.393 0 .729.253.994.544.259.265.591.436.953.436.828 0 1.5-.895 1.5-2s-.672-2-1.5-2c-.362 0-.694.17-.953.456-.265.29-.6.544-.994.544a.968.968 0 0 1-.974-1.024c.07-1.01.162-2.015.306-3.014a.75.75 0 0 0-.848-.848 39.655 39.655 0 0 0-3.014.306A.968.968 0 0 1 12 4.467Z" /></svg>}
              iconBg="#f3e8ff"
              count={nodes.length}
              onClick={() => setActiveSection('skills')}
            />
          </div>
        ) : activeSection === 'favorites' ? (
          /* Favorites view */
          favoriteNodes.length > 0 ? (
            favoriteNodes.map(node => (
              <NodeListItem
                key={node.id}
                node={node}
                onClick={() => handleNodeClick(node)}
                isFavorited={true}
                onToggleFavorite={toggleFavorite}
              />
            ))
          ) : (
            <div style={{ padding: '40px 24px', textAlign: 'center' }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth={1.5} style={{ width: '32px', height: '32px', margin: '0 auto 12px' }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
              </svg>
              <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>No favorites yet</div>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>
                Click the star on any skill to save it here
              </div>
            </div>
          )
        ) : activeSection === 'skills' ? (
          /* Skills — category submenu list */
          <div style={{ padding: '8px 0' }}>
            <BlankNodeItem onClick={() => handleNodeClick(makeBlankNode())} />
            {categories.filter(c => c.type !== 'custom').map(cat => {
              const catNodes = nodes.filter(n => n.type === cat.type);
              if (catNodes.length === 0) return null;
              return (
                <SectionHeader
                  key={cat.type}
                  title={cat.label}
                  description={cat.description}
                  icon={<span style={{ fontSize: '18px' }}>{cat.icon}</span>}
                  iconBg={typeColors[cat.type] + '18'}
                  count={catNodes.length}
                  onClick={() => setActiveSection(cat.type)}
                  accentColor={typeColors[cat.type]}
                />
              );
            })}
          </div>
        ) : inCategoryView ? (
          /* Category detail — show nodes for this type */
          <>
            {nodes.filter(n => n.type === activeSection).map(node => (
              <NodeListItem
                key={node.id}
                node={node}
                onClick={() => handleNodeClick(node)}
                isFavorited={favoriteIds.includes(node.id)}
                onToggleFavorite={toggleFavorite}
              />
            ))}
            {activeSection === 'custom' && nodes.filter(n => n.type === 'custom').length === 0 && (
              <div style={{ padding: '32px 24px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                No custom skills yet. Create one from the Skills menu.
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

function SectionHeader({ title, description, icon, iconBg, count, onClick, accentColor }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '14px 24px',
        cursor: 'pointer',
        borderBottom: '1px solid #f3f4f6',
        borderLeft: isHovered && accentColor ? `3px solid ${accentColor}` : '3px solid transparent',
        background: isHovered ? '#f9fafb' : 'transparent',
        transition: 'all 0.15s ease'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        background: iconBg || '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
          {title}
        </div>
        {description && (
          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
            {description}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
        {count != null && (
          <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>{count}</span>
        )}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill={isHovered ? '#6b7280' : '#d1d5db'} style={{ width: '16px', height: '16px', transition: 'fill 0.15s ease' }}>
          <path fillRule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.638l-3.96-4.158a.75.75 0 1 1 1.08-1.04l5.25 5.5a.75.75 0 0 1 0 1.08l-5.25 5.5a.75.75 0 1 1-1.08-1.04l3.96-4.158H3.75A.75.75 0 0 1 3 10Z" clipRule="evenodd" />
        </svg>
      </div>
    </div>
  );
}

function BlankNodeItem({ onClick }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e) => {
    const blankData = {
      ...blankNodeTemplate,
      id: `custom-skill-${Date.now()}`,
      color: typeColors[blankNodeTemplate.type] || '#6b7280'
    };
    e.dataTransfer.setData('application/json', JSON.stringify(blankData));
    e.dataTransfer.effectAllowed = 'copy';
    const el = e.currentTarget;
    const clone = el.cloneNode(true);
    clone.style.transform = 'rotate(3deg)';
    clone.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
    clone.style.background = '#fff';
    clone.style.width = el.offsetWidth + 'px';
    clone.style.position = 'absolute';
    clone.style.top = '-9999px';
    document.body.appendChild(clone);
    e.dataTransfer.setDragImage(clone, el.offsetWidth / 2, 30);
    setTimeout(() => document.body.removeChild(clone), 0);
    setIsDragging(true);
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={() => setIsDragging(false)}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '16px 24px',
        cursor: isDragging ? 'grabbing' : 'grab',
        marginBottom: '4px',
        background: isHovered ? '#f9fafb' : 'transparent',
        opacity: isDragging ? 0.4 : 1,
        borderBottom: '1px solid #f3f4f6',
        transition: 'all 0.15s ease'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        border: '2px dashed #d1d5db',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#9ca3af" style={{ width: '18px', height: '18px' }}>
          <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
        </svg>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: '14px', color: '#6b7280' }}>
          Custom Skill
        </div>
        <div style={{ fontSize: '12px', color: '#9ca3af', lineHeight: '1.4', marginTop: '1px' }}>
          Create a blank skill from scratch
        </div>
      </div>
    </div>
  );
}

function NodeListItem({ node, onClick, isFavorited, onToggleFavorite, compact }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const accentColor = typeColors[node.type] || '#6b7280';
  const category = categories.find(c => c.type === node.type);
  const categoryLabel = category?.label || 'Custom';

  const handleDragStart = (e) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      ...node,
      color: typeColors[node.type] || '#6b7280'
    }));
    e.dataTransfer.effectAllowed = 'copy';
    const el = e.currentTarget;
    const clone = el.cloneNode(true);
    clone.style.transform = 'rotate(3deg)';
    clone.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
    clone.style.background = '#fff';
    clone.style.borderRadius = '10px';
    clone.style.width = el.offsetWidth + 'px';
    clone.style.position = 'absolute';
    clone.style.top = '-9999px';
    document.body.appendChild(clone);
    e.dataTransfer.setDragImage(clone, el.offsetWidth / 2, 30);
    setTimeout(() => document.body.removeChild(clone), 0);
    setIsDragging(true);
  };

  const handleDragEnd = () => setIsDragging(false);

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={onClick}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: compact ? '10px 24px 10px 20px' : '14px 24px 14px 20px',
        cursor: isDragging ? 'grabbing' : 'grab',
        borderBottom: '1px solid #f3f4f6',
        borderLeft: isHovered ? `3px solid ${accentColor}` : '3px solid transparent',
        background: isHovered ? '#fafafa' : 'transparent',
        opacity: isDragging ? 0.4 : 1,
        transition: 'all 0.12s ease'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Favorite star button in top right */}
      <button
        onClick={(e) => onToggleFavorite(node.id, e)}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          fontSize: '11px',
          color: '#9ca3af',
          fontWeight: 600,
          background: '#fff',
          padding: '4px 8px',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
          cursor: 'pointer',
          transition: 'all 0.15s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#fefce8';
          e.currentTarget.style.borderColor = '#facc15';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#fff';
          e.currentTarget.style.borderColor = '#e5e7eb';
        }}
        title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      >
        {isFavorited ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#facc15" style={{ width: '14px', height: '14px' }}>
            <path fillRule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401Z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="#d1d5db" style={{ width: '14px', height: '14px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
          </svg>
        )}
      </button>

      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        background: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        flexShrink: 0
      }}>
        {node.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
          {node.name}
        </div>
        {!compact && (
          <div style={{
            fontSize: '12px',
            color: '#9ca3af',
            lineHeight: '1.4',
            marginTop: '2px'
          }}>
            {node.description}
          </div>
        )}
      </div>
      {/* Right arrow */}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill={isHovered ? '#6b7280' : '#d1d5db'} style={{ width: '16px', height: '16px', flexShrink: 0, transition: 'fill 0.15s ease' }}>
        <path fillRule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.638l-3.96-4.158a.75.75 0 1 1 1.08-1.04l5.25 5.5a.75.75 0 0 1 0 1.08l-5.25 5.5a.75.75 0 1 1-1.08-1.04l3.96-4.158H3.75A.75.75 0 0 1 3 10Z" clipRule="evenodd" />
      </svg>
    </div>
  );
}

export default NodePanel;
